import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/functions";
import firebaseApp from "services/firebase";
import { useForm, Controller } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  Alert,
  Link as MuiLink,
  IconButton,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ArrowBack,
  Google,
  MarkEmailRead,
  PhoneIphoneRounded,
} from "@mui/icons-material";
import {
  getAuth,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import PasswordRequirements from "./PasswordRequirements";
import { prefetchDeviceLinkSession } from "services/deviceLinkService";
import { isMorningReportDomain } from "features/morningReport/morningReportPaths";
import usePasswordRules from "./usePasswordRules";
import { ensureLearnerProfile } from "features/morningReport/learnerSignup";
import SixDigitCodeInput from "./SixDigitCodeInput";
import MfaDevHint from "./MfaDevHint";

export const STEPS = {
  EMAIL: 0, // Email entry + social buttons
  SIGN_IN: 1, // Existing account: password only
  SIGN_UP: 2, // New account: password + confirm
  FORGOT_PASSWORD: 3, // Send reset email (inline, no page redirect)
  MFA_CHALLENGE: 4, // TOTP or SMS second factor during sign-in
  EMAIL_VERIFY: 5, // Wait for email verification after sign-up
};

/**
 * AuthFlow - Unified sign-in/sign-up flow.
 *
 * Step 0 (EMAIL): Email entry + Google social button.
 *   On Continue, checks fetchSignInMethodsForEmail first (instant, needs Email
 *   Enumeration Protection off), falling back to the `checkEmailExists` Cloud
 *   Function (Admin SDK) only when that comes back empty. See accountExists().
 * Step 1 (SIGN_IN): Existing user — password only.
 * Step 2 (SIGN_UP): New user — password + confirm.
 * Step 3+ handles email verification and MFA challenge only.
 *
 * When prefilledEmail is provided (invitation flow), skips EMAIL and checks
 * immediately on mount, dropping into SIGN_IN or SIGN_UP.
 */
const AuthFlow = ({
  onClose,
  onSuccess,
  onStepChange,
  backHandlerRef,
  inDialog = false,
  prefilledEmail = null,
  disableOAuth = false,
  skipEmailVerification = false,
  initialStep = STEPS.EMAIL,
  allowSignup = false,
  topNotice = null,
  shareCode = null,
  // "learner" for a MorningReport signup: the profile is written as soon as the
  // account exists, so the role can't be lost on the email-verification trip.
  signupRole = null,
  // One headline for the email, sign-in and sign-up steps (AuthPage): the same
  // journey, so only the subtitle under it changes.
  heroTitle = null,
  // AuthPage: "Back" as a borderless button under the primary one, instead of an
  // arrow above the heading.
  inlineBack = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("account");
  const passwordRules = usePasswordRules();
  const [step, setStepInternal] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmedEmail, setConfirmedEmail] = useState(prefilledEmail || "");
  const [prefilledResolving, setPrefilledResolving] = useState(
    Boolean(prefilledEmail && initialStep === STEPS.EMAIL),
  );
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [phoneLinkLoading, setPhoneLinkLoading] = useState(false);
  const [verifyEmailSending, setVerifyEmailSending] = useState(false);
  const [verifyEmailSent, setVerifyEmailSent] = useState(false);
  // MFA challenge state
  const [mfaHint, setMfaHint] = useState(null);
  const [mfaType, setMfaType] = useState(null); // 'totp' | 'phone'
  const [mfaCode, setMfaCode] = useState("");
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const mfaResolverRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  const mfaCodeInputRef = useRef(null);
  const emailForm = useForm({
    mode: "onSubmit",
    defaultValues: { email: prefilledEmail || "" },
  });

  const passwordForm = useForm({
    mode: "onSubmit",
    defaultValues: { password: "", confirmPassword: "" },
  });

  const watchPassword = passwordForm.watch("password");

  // Report external step for dialog back-button visibility.
  // prefilledEmail flows treat SIGN_IN/SIGN_UP as step 0 (no back needed).
  const reportStep = (s) => {
    if (prefilledEmail && (s === STEPS.SIGN_IN || s === STEPS.SIGN_UP)) {
      onStepChange?.(0);
    } else if (s === STEPS.EMAIL_VERIFY) {
      // Show back button (lets them cancel and restart with a different email)
      onStepChange?.(STEPS.EMAIL_VERIFY);
    } else {
      onStepChange?.(s);
    }
  };

  const setStep = (s) => {
    setStepInternal(s);
    reportStep(s);
  };

  // AuthPage can mount before user context is fully hydrated on hard reload.
  // If initialStep arrives late (e.g. EMAIL_VERIFY), advance from EMAIL.
  useEffect(() => {
    if (initialStep == null) return;
    if (
      step === STEPS.EMAIL &&
      initialStep !== STEPS.EMAIL &&
      !prefilledEmail
    ) {
      setStep(initialStep);
    }
  }, [initialStep, step, prefilledEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reportStep(step);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When prefilledEmail is provided, check it on mount and skip EMAIL step
  useEffect(() => {
    if (prefilledEmail && initialStep === STEPS.EMAIL) {
      resolveEmailStep(prefilledEmail);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // EMAIL_VERIFY: poll for verification; auto-advance when the link is clicked
  useEffect(() => {
    if (step !== STEPS.EMAIL_VERIFY) return;
    const user = firebaseApp.auth().currentUser;
    if (!user) return;

    // Interval reload — catches verification even if tab was backgrounded
    const interval = setInterval(async () => {
      try {
        await user.reload();
        if (firebaseApp.auth().currentUser?.emailVerified) {
          clearInterval(interval);
          finishSignIn(firebaseApp.auth().currentUser);
        }
      } catch (e) {
        /* ignore */
      }
    }, 3000);

    // onIdTokenChanged fires immediately when Firebase detects the verification
    const unsubscribe = firebaseApp.auth().onIdTokenChanged((u) => {
      if (u?.emailVerified) {
        clearInterval(interval);
        unsubscribe();
        finishSignIn(u);
      }
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = async () => {
    if (step === STEPS.SIGN_IN || step === STEPS.SIGN_UP) {
      if (!prefilledEmail) {
        passwordForm.reset();
        setError("");
        setStep(STEPS.EMAIL);
      }
    } else if (step === STEPS.FORGOT_PASSWORD) {
      setResetEmailSent(false);
      setError("");
      setStep(STEPS.SIGN_IN);
    } else if (step === STEPS.MFA_CHALLENGE) {
      mfaResolverRef.current = null;
      setMfaHint(null);
      setMfaType(null);
      setMfaCode("");
      setMfaVerificationId(null);
      setError("");
      setStep(STEPS.SIGN_IN);
    } else if (step === STEPS.EMAIL_VERIFY) {
      // Cancel verification — sign out and restart
      try {
        await firebaseApp.auth().signOut();
      } catch (e) {
        /* ignore */
      }
      setVerifyEmailSent(false);
      setConfirmedEmail("");
      setError("");
      setStep(STEPS.EMAIL);
    }
  };

  useEffect(() => {
    if (backHandlerRef) backHandlerRef.current = handleBack;
  });

  const finishSignIn = (user) => {
    const currentUser = user || firebaseApp.auth().currentUser;
    if (onSuccess) onSuccess(currentUser);
    else navigate("/dashboard");
  };

  // For share-link providers, bake the share return into the verification email
  // itself (continueUrl). This is the ONLY context that survives the email
  // round-trip when the link is opened on another device or in a mail app's
  // in-app browser, where localStorage breadcrumbs are gone. /verify reads it.
  // A morningreport.si signup is sent back there after verifying, since the
  // verify link itself always opens on the primary domain.
  const buildVerifyActionSettings = () => {
    if (shareCode) {
      return {
        url: `${window.location.origin}/share?c=${shareCode}`,
        handleCodeInApp: false,
      };
    }
    if (isMorningReportDomain()) {
      return {
        url: `${window.location.origin}/onboarding?role=learner`,
        handleCodeInApp: false,
      };
    }
    return undefined;
  };

  // Send the verification email, preferring the share continueUrl. If the app's
  // custom domain isn't in Firebase Auth's Authorized domains, sendOobCode rejects
  // the continueUrl with a 400 (auth/unauthorized-continue-uri) — fall back to a
  // plain email so the user is NEVER blocked from verifying. They just lose the
  // cross-device deep link until the domain is authorized in the console.
  const sendVerificationEmail = async (targetUser) => {
    const settings = buildVerifyActionSettings();
    if (!settings) {
      await targetUser.sendEmailVerification();
      return;
    }
    try {
      await targetUser.sendEmailVerification(settings);
    } catch (e) {
      if (
        e?.code === "auth/unauthorized-continue-uri" ||
        e?.code === "auth/invalid-continue-uri"
      ) {
        // Loud on purpose: this silently cost morningreport.si learners their
        // return link. Add the domain to Firebase Auth's Authorized domains.
        console.error(
          `Verification email sent WITHOUT its continueUrl (${settings.url}): ${e.code}`,
        );
        await targetUser.sendEmailVerification();
        return;
      }
      throw e;
    }
  };

  const goToEmailVerify = async (user) => {
    setVerifyEmailSending(true);
    setVerifyEmailSent(false);
    setError("");
    try {
      await sendVerificationEmail(user);
    } catch (e) {
      // Rate-limited or already sent — still advance to the step
    } finally {
      setVerifyEmailSending(false);
    }
    setStep(STEPS.EMAIL_VERIFY);
  };

  const handleAuthSuccess = async (user) => {
    finishSignIn(user);
  };

  // Prefer TOTP, fall back to phone. For phone, sends SMS before transitioning
  // so the Sign In button stays in loading state — no intermediate blank screen.
  const enterMfaChallenge = async (resolverObj) => {
    mfaResolverRef.current = resolverObj;
    const hint =
      resolverObj.hints.find((h) => h.factorId === "totp") ||
      resolverObj.hints.find((h) => h.factorId === "phone") ||
      resolverObj.hints[0];
    setMfaCode("");
    setError("");

    if (hint.factorId === "phone") {
      setMfaHint(hint);
      setMfaType("phone");
      try {
        const auth = getAuth();
        if (!recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current = new RecaptchaVerifier(
            auth,
            recaptchaContainerRef.current,
            { size: "invisible" },
          );
        }
        const provider = new PhoneAuthProvider(auth);
        const vid = await provider.verifyPhoneNumber(
          { multiFactorHint: hint, session: resolverObj.session },
          recaptchaVerifierRef.current,
        );
        setMfaVerificationId(vid);
        setStep(STEPS.MFA_CHALLENGE);
      } catch (err) {
        setError(t("errors.sendCodeFailed"));
        mfaResolverRef.current = null;
        setMfaHint(null);
        setMfaType(null);
      }
    } else {
      setMfaHint(hint);
      setMfaType(hint.factorId);
      setStep(STEPS.MFA_CHALLENGE);
    }
  };

  // Focus the code input when the MFA challenge step becomes active
  useEffect(() => {
    if (step === STEPS.MFA_CHALLENGE) {
      setTimeout(() => mfaCodeInputRef.current?.focus(), 100);
    }
  }, [step]);

  const handleMfaVerify = async (code = mfaCode) => {
    setLoading(true);
    setError("");
    try {
      const auth = getAuth();
      const resolver = mfaResolverRef.current;
      let assertion;

      if (mfaType === "totp") {
        assertion = TotpMultiFactorGenerator.assertionForSignIn(
          mfaHint.uid,
          code,
        );
      } else {
        const credential = PhoneAuthProvider.credential(
          mfaVerificationId,
          code,
        );
        assertion = PhoneMultiFactorGenerator.assertion(credential);
      }

      const result = await resolver.resolveSignIn(assertion);
      await handleAuthSuccess(result.user);
    } catch (err) {
      if (
        err.code === "auth/invalid-verification-code" ||
        err.code === "auth/invalid-verification-id"
      ) {
        setError(t("errors.incorrectCode"));
      } else {
        setError(err.message || t("errors.verificationFailed"));
      }
      setMfaCode("");
      mfaCodeInputRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleResendSms = async () => {
    setSmsSending(true);
    setSmsSent(false);
    setMfaVerificationId(null);
    setError("");
    try {
      const auth = getAuth();
      // clear() is async internally — grecaptcha tracks elements by reference,
      // not DOM state, so reusing the same node always fails. Fresh node every time.
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
      const freshNode = document.createElement("div");
      document.body.appendChild(freshNode);
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, freshNode, {
        size: "invisible",
      });
      const provider = new PhoneAuthProvider(auth);
      const vid = await provider.verifyPhoneNumber(
        { multiFactorHint: mfaHint, session: mfaResolverRef.current.session },
        recaptchaVerifierRef.current,
      );
      setMfaVerificationId(vid);
      setSmsSent(true);
      mfaCodeInputRef.current?.reset();
    } catch (err) {
      console.error("[MFA resend]", err.code, err.message, err);
      setError(t("errors.resendCodeFailed"));
    } finally {
      setSmsSending(false);
    }
  };

  // Fast path: ask Firebase Auth directly (~100ms). With Email Enumeration
  // Protection off, a non-empty list means the account exists. An empty list is
  // ambiguous (protection on, or an Admin-created account with no provider yet),
  // so only then fall back to the `checkEmailExists` Cloud Function, which pays
  // a multi-second cold start. Existing users never hit the fallback.
  const accountExists = async (email) => {
    try {
      const methods = await firebaseApp
        .auth()
        .fetchSignInMethodsForEmail(email);
      if (methods.length > 0) return true;
    } catch (err) {
      // Fall through to the Admin SDK check.
    }
    const checkEmailExists = firebase
      .functions()
      .httpsCallable("checkEmailExists");
    const { data } = await checkEmailExists({ email });
    return data.exists;
  };

  const resolveEmailStep = async (email) => {
    setLoading(true);
    setError("");
    try {
      const exists = await accountExists(email);
      setConfirmedEmail(email);
      if (!exists && process.env.REACT_APP_INVITE_ONLY === "true" && !allowSignup) {
        setError(t("authFlow.inviteOnly"));
        return;
      }
      setStep(exists ? STEPS.SIGN_IN : STEPS.SIGN_UP);
    } catch (err) {
      setError(t("authFlow.verifyEmailFailed"));
    } finally {
      setLoading(false);
      setPrefilledResolving(false);
    }
  };

  const handleEmailContinue = (data) => resolveEmailStep(data.email);

  const handleSignIn = async (data) => {
    setLoading(true);
    setError("");
    try {
      const result = await firebaseApp
        .auth()
        .signInWithEmailAndPassword(confirmedEmail, data.password);
      await handleAuthSuccess(result.user, false);
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        const auth = getAuth();
        await enterMfaChallenge(getMultiFactorResolver(auth, err));
      } else if (
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-credential"
      ) {
        setError(t("errors.incorrectPassword"));
      } else if (err.code === "auth/network-request-failed") {
        setError(t("errors.network"));
      } else {
        setError(err.message || t("errors.signInFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (data) => {
    setLoading(true);
    setError("");
    try {
      const result = await firebaseApp
        .auth()
        .createUserWithEmailAndPassword(confirmedEmail, data.password);
      if (signupRole === "learner") await ensureLearnerProfile(result.user);
      if (skipEmailVerification || result.user?.emailVerified) {
        finishSignIn(result.user);
      } else {
        await goToEmailVerify(result.user);
      }
    } catch (err) {
      if (err.code === "auth/weak-password") {
        setError(t("validation.passwordMin6"));
      } else if (err.code === "auth/email-already-in-use") {
        setError(t("authFlow.emailInUse"));
      } else {
        setError(err.message || t("authFlow.signUpFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider) => {
    setLoading(true);
    setError("");
    try {
      const result = await firebaseApp.auth().signInWithPopup(provider);
      if (signupRole === "learner" && result.additionalUserInfo?.isNewUser) {
        await ensureLearnerProfile(result.user);
      }
      setConfirmedEmail(result.user.email || "");
      await handleAuthSuccess(result.user);
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        const auth = getAuth();
        setConfirmedEmail(""); // email unknown until MFA resolves
        await enterMfaChallenge(getMultiFactorResolver(auth, err));
      } else if (err.code !== "auth/popup-closed-by-user") {
        setError(err.message || t("errors.signInFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () =>
    handleOAuthSignIn(new firebase.auth.GoogleAuthProvider());

  const handleResendVerification = async () => {
    setVerifyEmailSending(true);
    setVerifyEmailSent(false);
    setError("");
    try {
      await sendVerificationEmail(firebaseApp.auth().currentUser);
      setVerifyEmailSent(true);
    } catch (err) {
      setError(t("authFlow.resendVerificationFailed"));
    } finally {
      setVerifyEmailSending(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setError("");
    try {
      await firebaseApp.auth().sendPasswordResetEmail(confirmedEmail);
      setResetEmailSent(true);
    } catch (err) {
      setError(t("authFlow.resetEmailFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Reset goes back to the password step (handleBack); the email step is the start.
  const showBack =
    ((step === STEPS.SIGN_IN || step === STEPS.SIGN_UP) && !prefilledEmail) ||
    step === STEPS.FORGOT_PASSWORD;

  const inlineBackButton = inlineBack && showBack && (
    <Button
      fullWidth
      size="large"
      onClick={handleBack}
      sx={{ mt: 1 }}
    >
      {t("common.back")}
    </Button>
  );

  // On its own page (AuthPage) the sign-in screen is the whole view, so its type
  // steps up a size; in a dialog it stays compact.
  const textVariants = inDialog
    ? { heading: "h5", subtitle: "body2", small: "caption" }
    : { heading: "h3", subtitle: "body1", small: "body2" };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: inDialog ? "100%" : 440,
        mx: "auto",
        p: inDialog ? 0 : 3,
        position: "relative",
      }}
    >
      {/* Invisible recaptcha container for SMS MFA */}
      <div ref={recaptchaContainerRef} />

      {!inDialog && !inlineBack && showBack && (
        <Box sx={{ mb: 2 }}>
          <IconButton onClick={handleBack} edge="start" sx={{ ml: -1 }}>
            <ArrowBack />
          </IconButton>
        </Box>
      )}

      {/* Step 0: Email + social */}
      {step === STEPS.EMAIL && (
        <Box>
          {prefilledEmail && prefilledResolving ? (
            <Box
              sx={{
                minHeight: inDialog ? 220 : 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t("authFlow.preparing")}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                variant={textVariants.heading}
                sx={{ mb: 1, textAlign: "center" }}
              >
                {heroTitle || t("authFlow.welcome")}
              </Typography>
              <Typography
                variant={textVariants.subtitle}
                color="text.secondary"
                sx={{ mb: 3, textAlign: "center" }}
              >
                {t("authFlow.emailSubtitle")}
              </Typography>
              {/* Under the heading and subtitle, right above the form. */}
              {topNotice}

              <form
                onSubmit={emailForm.handleSubmit(handleEmailContinue)}
                noValidate
              >
                <Controller
                  name="email"
                  control={emailForm.control}
                  rules={{
                    required: t("validation.emailRequired"),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t("validation.emailInvalid"),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("common.emailAddress")}
                      type="email"
                      fullWidth
                      error={!!emailForm.formState.errors.email}
                      helperText={emailForm.formState.errors.email?.message}
                      autoFocus
                      sx={{ mb: 2 }}
                    />
                  )}
                />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  loading={loading}
                  sx={{ py: 1.5, mb: 1 }}
                >
                  {t("common.continue")}
                </LoadingButton>
              </form>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {/* Google sign-in is hidden until it has been tested end to end: the
                  OAuth path skips the checkEmailExists gate that the email path
                  runs, so its behaviour on the invite-only gate and on a first-time
                  account is unverified. handleGoogleSignIn and handleOAuthSignIn are
                  left in place below — restoring this is uncommenting the block.
              {!disableOAuth && (
                <>
                  <Divider sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      or
                    </Typography>
                  </Divider>

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    startIcon={<Google />}
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    Continue with Google
                  </Button>
                </>
              )}
              */}

              {/* Above the legal line, because it is a way to sign in and belongs
                  with the others rather than in the small print under them. */}
              <Divider sx={{ my: 2 }}>
                <Typography variant={textVariants.small} color="text.secondary">
                  {t("common.or")}
                </Typography>
              </Divider>
              {/* Opens the sign-in session before leaving, so the QR is ready
                  when the phone page appears instead of loading in there. */}
              <LoadingButton
                size="large"
                fullWidth
                loading={phoneLinkLoading}
                startIcon={<PhoneIphoneRounded />}
                onClick={async () => {
                  setPhoneLinkLoading(true);
                  try {
                    await prefetchDeviceLinkSession();
                  } catch (err) {
                    // The phone page opens its own session and shows the error.
                  }
                  navigate("/link-device");
                }}
                sx={{ py: 1.5 }}
              >
                {t("authFlow.signInWithPhone")}
              </LoadingButton>

              <Typography
                variant={textVariants.small}
                color="text.secondary"
                sx={{ mt: 2, textAlign: "center", display: "block" }}
              >
                <Trans
                  ns="account"
                  i18nKey="authFlow.agreeContinue"
                  components={{
                    1: <MuiLink href="/terms-of-use" target="_blank" />,
                    3: <MuiLink href="/privacy-policy" target="_blank" />,
                  }}
                />
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Step 1: Sign In */}
      {step === STEPS.SIGN_IN && (
        <Box>
          <Typography
            variant={textVariants.heading}
            sx={{ mb: 1, textAlign: "center" }}
          >
            {heroTitle || t("common.signIn")}
          </Typography>
          <Typography
            variant={textVariants.subtitle}
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            {t("authFlow.signInSubtitle")}
          </Typography>
          {topNotice}

          <form onSubmit={passwordForm.handleSubmit(handleSignIn)} noValidate>
            <TextField
              label={t("common.emailAddress")}
              value={confirmedEmail}
              fullWidth
              disabled
              sx={{ mb: 2 }}
            />

            <Controller
              name="password"
              control={passwordForm.control}
              rules={{ required: t("validation.passwordRequired") }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("common.password")}
                  type="password"
                  fullWidth
                  error={!!passwordForm.formState.errors.password}
                  helperText={passwordForm.formState.errors.password?.message}
                  autoFocus
                  sx={{ mb: 3 }}
                />
              )}
            />

            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              loading={loading}
              sx={{ py: 1.5 }}
            >
              {t("common.signIn")}
            </LoadingButton>
          </form>
          {inlineBackButton}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
            <MuiLink
              component="button"
              type="button"
              onClick={() => setStep(STEPS.FORGOT_PASSWORD)}
              underline="always"
              sx={{ fontSize: "0.875rem" }}
            >
              {t("authFlow.forgotPassword")}
            </MuiLink>
          </Typography>
        </Box>
      )}

      {/* Step 2: Sign Up */}
      {step === STEPS.SIGN_UP && (
        <Box>
          <Typography
            variant={textVariants.heading}
            sx={{ mb: 1, textAlign: "center" }}
          >
            {heroTitle || t("authFlow.createAccountTitle")}
          </Typography>
          <Typography
            variant={textVariants.subtitle}
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            {t("authFlow.signUpSubtitle")}
          </Typography>
          {topNotice}

          <form onSubmit={passwordForm.handleSubmit(handleSignUp)} noValidate>
            <TextField
              label={t("common.emailAddress")}
              value={confirmedEmail}
              fullWidth
              disabled
              sx={{ mb: 2 }}
            />

            <Controller
              name="password"
              control={passwordForm.control}
              rules={passwordRules}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("common.password")}
                  type="password"
                  fullWidth
                  error={!!passwordForm.formState.errors.password}
                  helperText={passwordForm.formState.errors.password?.message}
                  autoFocus
                  sx={{ mb: 1 }}
                />
              )}
            />

            <PasswordRequirements value={watchPassword} sx={{ mb: 3 }} />

            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              loading={loading}
              sx={{ py: 1.5 }}
            >
              {t("authFlow.createAccount")}
            </LoadingButton>
          </form>
          {inlineBackButton}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Typography
            variant={textVariants.small}
            color="text.secondary"
            sx={{ mt: 3, textAlign: "center", display: "block" }}
          >
            <Trans
              ns="account"
              i18nKey="authFlow.agreeCreate"
              components={{
                1: <MuiLink href="/terms-of-use" target="_blank" />,
                3: <MuiLink href="/privacy-policy" target="_blank" />,
              }}
            />
          </Typography>
        </Box>
      )}

      {/* Step 3: Forgot Password */}
      {step === STEPS.FORGOT_PASSWORD && (
        <Box>
          <Typography
            variant={textVariants.heading}
            sx={{ mb: 1, textAlign: "center" }}
          >
            {t("authFlow.resetTitle")}
          </Typography>
          <Typography
            variant={textVariants.subtitle}
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            {resetEmailSent
              ? t("authFlow.resetCheckInbox")
              : t("authFlow.resetIntro")}
          </Typography>

          {!resetEmailSent && (
            <>
              <TextField
                label={t("common.emailAddress")}
                value={confirmedEmail}
                fullWidth
                disabled
                sx={{ mb: 3 }}
              />

              <LoadingButton
                variant="contained"
                size="large"
                fullWidth
                loading={loading}
                onClick={handleForgotPassword}
                sx={{ py: 1.5 }}
              >
                {t("authFlow.sendResetLink")}
              </LoadingButton>
            </>
          )}

          {resetEmailSent && (
            <Alert severity="success">
              {t("authFlow.resetLinkSent", { email: confirmedEmail })}
            </Alert>
          )}
          {inlineBackButton}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
        </Box>
      )}

      {/* Step 4: MFA Challenge */}
      {step === STEPS.MFA_CHALLENGE && (
        <Box>
          <Typography
            variant={textVariants.heading}
            sx={{ mb: 1, textAlign: "center" }}
          >
            {mfaType === "totp"
              ? t("authFlow.mfaTotpTitle")
              : t("authFlow.mfaSmsTitle")}
          </Typography>
          <Typography
            variant={textVariants.subtitle}
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            {mfaType === "totp"
              ? t("mfaChallenge.enterTotp")
              : t("mfaChallenge.enterSms", {
                  phone: mfaHint?.phoneNumber || t("mfaChallenge.yourPhone"),
                })}
          </Typography>

          <MfaDevHint />

          <SixDigitCodeInput
            ref={mfaCodeInputRef}
            onComplete={handleMfaVerify}
            onChange={setMfaCode}
            disabled={loading || smsSending}
            autoFocus
          />

          <LoadingButton
            variant="contained"
            size="large"
            fullWidth
            loading={loading}
            disabled={mfaCode.length < 6 || smsSending}
            onClick={() => handleMfaVerify(mfaCode)}
            sx={{ py: 1.5, mt: 3 }}
          >
            {t("common.verify")}
          </LoadingButton>

          {mfaType === "phone" && (mfaVerificationId || smsSending) && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              {smsSent && (
                <Alert
                  severity="success"
                  sx={{ mb: 1.5, textAlign: "left" }}
                  onClose={() => setSmsSent(false)}
                >
                  {t("mfaChallenge.newCodeHasBeenSent")}
                </Alert>
              )}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {smsSending ? (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      color: "text.secondary",
                    }}
                  >
                    <CircularProgress size={12} />
                    {t("mfaChallenge.sendingNewCode")}
                  </Box>
                ) : (
                  <>
                    {t("mfaChallenge.codeDelay")}
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={handleResendSms}
                      underline="always"
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {t("mfaChallenge.resendNewCode")}
                    </MuiLink>
                  </>
                )}
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Typography
            variant={textVariants.small}
            color="text.secondary"
            sx={{ mt: 3, textAlign: "center", display: "block" }}
          >
            {t("authFlow.cantLogIn")}{" "}
            <MuiLink href="mailto:support@md3c.com" underline="always">
              {t("common.contactSupport")}
            </MuiLink>
          </Typography>
        </Box>
      )}

      {/* Step 6: Email verification */}
      {step === STEPS.EMAIL_VERIFY && (
        <Box sx={{ textAlign: "center" }}>
          <MarkEmailRead
            sx={{ fontSize: 52, color: "primary.main", mb: 1.5 }}
          />
          <Typography variant={textVariants.heading} sx={{ mb: 1 }}>
            {t("authFlow.verifyTitle")}
          </Typography>
          <Typography
            variant={textVariants.subtitle}
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            <Trans
              ns="account"
              i18nKey="authFlow.verifySent"
              values={{ email: confirmedEmail }}
              components={{
                1: <Box component="span" fontWeight={600} color="text.primary" />,
              }}
            />
          </Typography>

          {verifyEmailSent && (
            <Alert severity="success" sx={{ mb: 2, textAlign: "left" }}>
              {t("authFlow.verifyResent")}
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, textAlign: "left" }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          <LoadingButton
            variant="outlined"
            fullWidth
            loading={verifyEmailSending}
            onClick={handleResendVerification}
            sx={{ mb: 1.5 }}
          >
            {t("authFlow.resendEmail")}
          </LoadingButton>
        </Box>
      )}

      {/* Only meaningful once an account exists (EMAIL_VERIFY) — there's no
          session to end on the email/password steps, so hide it there. */}
      {step === STEPS.EMAIL_VERIFY && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ mt: 3, textAlign: "center", display: "block" }}
        >
          <MuiLink
            component="button"
            variant="caption"
            color="text.disabled"
            underline="always"
            onClick={async () => {
              try {
                await firebaseApp.auth().signOut();
              } catch (e) {
                /* ignore */
              }
              setStep(STEPS.EMAIL);
              setConfirmedEmail("");
              setError("");
            }}
          >
            {t("common.logOut")}
          </MuiLink>
        </Typography>
      )}
    </Box>
  );
};

export default AuthFlow;
